import { METRICS_PATH, STATISTICS_PATH } from '@/constants';
import { formatSlug } from '@/payload/fields/slug/formatSlug';
import {
  type Header,
  type IntroBlock,
  type Page,
  type TopicBlock,
} from '@/payload/payload.types';

export type ParsedLinkData = {
  type: NonNullable<LinkGroup['link']>['type'];
  name: string;
  href: string | null | undefined;
  links: (Omit<ParsedLinkData, 'links'> & { id: string })[];
  newTab: boolean;
  scroll?: boolean;
  headerKey?: string;
};

type LinkGroup =
  | NonNullable<NonNullable<Header['navLinks']>[number]['linkGroup']>[number]
  | NonNullable<NonNullable<IntroBlock['columns']>[number]>;

function lastBreadcrumb(breadcrumbs: Page['breadcrumbs']) {
  if (!breadcrumbs) return undefined;

  return breadcrumbs[breadcrumbs.length - 1];
}

function generatedLink(
  link: LinkGroup['link'] | null,
  includeLayoutLinks: boolean = false,
): ParsedLinkData | null {
  if (!link) return null;

  const generated =
    link.type === 'generate' &&
    !!link.generated &&
    typeof link.generated === 'object'
      ? link.generated
      : null;

  if (!generated) return null;

  const category =
    typeof generated.category === 'object' ? generated.category : null;

  const breadcrumb = lastBreadcrumb(generated.breadcrumbs);

  const href = breadcrumb?.url;

  const name = category ? category.name : breadcrumb?.label || 'Nimetu';

  const links = includeLayoutLinks
    ? (
        generated.layout?.filter(
          (block) => block.blockType === 'topic-block' && !block.hideFromMenu,
        ) as TopicBlock[]
      ).map((block) => {
        if (
          block.type === 'linked-category' &&
          !!block.category &&
          typeof block.category === 'object'
        ) {
          const name = block.overrideTitle
            ? block.title!
            : !!block.category && typeof block.category === 'object'
              ? block.category.name
              : block.title || 'Nimetu';

          return {
            type: undefined,
            newTab: false,
            id: block.id!,
            name,
            href: href ? (href || '/') + `#${formatSlug(name)}` : undefined,
            scroll: false,
          };
        }

        if (block.type === 'manual') {
          const name = block.title || '';

          return {
            newTab: false,
            type: undefined,
            id: block.id!,
            name,
            href: href ? (href || '/') + `#${formatSlug(name)}` : undefined,
            scroll: false,
          };
        }
      }) || []
    : [];

  return {
    type: link.type,
    newTab: false,
    name,
    href,
    links: links.filter((link) => !!link),
  };
}

function parseNoLink(link: LinkGroup['link'] | null): ParsedLinkData | null {
  if (!link) return null;

  if (link.type !== 'none' || !link.label) {
    return null;
  }

  return {
    type: link.type,
    newTab: !!link.newTab,
    name: link.label,
    href: undefined,
    links: [],
  };
}

function parseInternalLink(
  link: LinkGroup['link'] | null,
): ParsedLinkData | null {
  if (!link) return null;

  if (link.type !== 'reference') {
    return null;
  }

  const ref = link.reference;

  if (ref) {
    if (typeof ref.value === 'number') {
      return null;
    }

    if (ref.relationTo === 'pages' && ref.value.pageType !== 'placeholder') {
      const breadcrumb = lastBreadcrumb(ref.value.breadcrumbs);

      const name =
        link.label || ref.value.titlePlainText || breadcrumb?.label || '';

      if (!name) {
        return null;
      }

      return {
        type: link.type,
        newTab: !!link.newTab,
        name: name,
        href: breadcrumb?.url,
        links: [],
      };
    }

    if (ref.relationTo === 'metrics' || ref.relationTo === 'statistics') {
      const name = link.label || ref.value.titlePlainText || '';

      if (!name) {
        return null;
      }

      return {
        type: link.type,
        newTab: !!link.newTab,
        name,
        href:
          ref.relationTo === 'metrics'
            ? METRICS_PATH + '/' + ref.value.id
            : STATISTICS_PATH + '/' + ref.value.id,
        links: [],
      };
    }
  }

  return null;
}

function parseCustomLink(
  link: LinkGroup['link'] | null,
): ParsedLinkData | null {
  if (!link) return null;

  if (link.type !== 'custom' || !link.label) {
    return null;
  }

  return {
    type: link.type,
    newTab: !!link.newTab,
    name: link.label,
    href: link.url,
    links: [],
  };
}

function parsePredefinedLink(
  link: LinkGroup['link'] | null,
): ParsedLinkData | null {
  if (!link) return null;

  if (link.type !== 'predefined' || !link.label || !link.internal) {
    return null;
  }

  return {
    type: link.type,
    newTab: !!link.newTab,
    name: link.label,
    href: link.internal,
    links: [],
  };
}

function parseHeaderLink(
  link: LinkGroup['link'] | null,
): ParsedLinkData | null {
  if (!link) return null;

  if (link.type !== 'header' || !link.headerKey || !link.label) {
    return null;
  }

  return {
    type: link.type,
    newTab: false,
    name: link.label,
    href: '',
    links: [],
    headerKey: link.headerKey,
  };
}

export function parseLink(
  link: LinkGroup['link'] | null,
  includeLayoutLinks: boolean = false,
): ParsedLinkData | null {
  return (
    parseHeaderLink(link) ||
    parseNoLink(link) ||
    parseInternalLink(link) ||
    parseCustomLink(link) ||
    parsePredefinedLink(link) ||
    generatedLink(link, includeLayoutLinks)
  );
}
